import type { Warning } from '@/components/common/WarningIcon'

import { getVaultWarningTypeMessage } from '@/helpers/warningMessages'
import { getVaultWarningScrollType } from '@/helpers/warnings'
import { WarningLevel } from '@/types/enums'

import type { VaultListingTableRow } from './config/VaultListing.types'
import type { VaultListingRow } from './vaultListing.contract'

const WARNING_LEVELS: Partial<Record<string, Warning['level']>> = {
  red: WarningLevel.Red,
  yellow: WarningLevel.Yellow
}

const toWarnings = (warnings: VaultListingRow['warnings']): Warning[] =>
  warnings.flatMap(({ type, level, content, delisted }) => {
    const warningLevel = WARNING_LEVELS[level]
    const message = getVaultWarningTypeMessage(type, { delisted, content })
    return warningLevel && message
      ? [{ level: warningLevel, message, warningType: getVaultWarningScrollType(type) }]
      : []
  })

const toTableRow = (row: VaultListingRow): VaultListingTableRow => ({
  isVaultV2: row.version === 'v2',
  vault: {
    address: row.address,
    name: row.name,
    chainId: row.chainId,
    assetLogoUri: row.assetLogoUri,
    warnings: toWarnings(row.warnings),
    curators: row.curators.map(({ id, name, imageUri }) => ({ id, name, image: imageUri }))
  },
  allocation: { totalSupplyAssetsUsd: row.supplyAssetsUsd }
})

/** The table rows, dust allocations left out, largest USD allocation first and unpriced ones last. */
export const getVaultListingRows = (rows: VaultListingRow[]): VaultListingTableRow[] =>
  rows
    .filter(row => !row.isDust)
    .map(toTableRow)
    .toSorted(
      (a, b) =>
        (b.allocation.totalSupplyAssetsUsd ?? -Infinity) -
        (a.allocation.totalSupplyAssetsUsd ?? -Infinity)
    )
